/**
 * Slider di `sections/lab-slider.liquid` di 120''LAB (custom element `<lab-slider>`).
 *
 * Il markup e il CSS stanno nella sezione: la pista `[data-lab-track]` e uno scroller scroll-snap
 * (su desktop, con la disposizione "griglia", una griglia statica che non scorre). Questo modulo,
 * senza librerie:
 * - calcola le posizioni di scorrimento (una per slide meno le colonne visibili piu una), con le stesse
 *   larghezze del CSS: passo = larghezza della slide + distanza;
 * - gestisce frecce (`[data-lab-prev]` / `[data-lab-next]`) e pallini (`[data-lab-dots]`, creati qui);
 *   se non c'e overflow la classe `is-scrollable` manca: il CSS nasconde pallini e frecce sotto, mentre le frecce ai
 *   lati restano visibili ma disabilitate (come nel vecchio slider);
 * - "loop": dopo l'ultima posizione torna alla prima (e "precedente" sulla prima va all'ultima);
 * - autoplay: avanza di una posizione ogni N ms, in pausa con mouse o focus dentro, durante il tocco,
 *   a scheda nascosta e fuori dallo schermo; dove la pista non scorre (griglia su desktop) non fa nulla;
 * - altezza adattiva (solo mobile): il contenitore prende l'altezza delle slide visibili;
 * - video `[data-lab-video]`: la sorgente (`source[data-src]`) viene assegnata quando il video si avvicina
 *   al viewport (margine 200px), parte muto in loop e si ferma fuori vista; il tasto `[data-lab-sound]`
 *   attiva/disattiva l'audio, uno solo alla volta in tutta la pagina, e l'audio si spegne quando il
 *   video esce dalla vista;
 * - animazione d'ingresso: quando il componente entra nella finestra mostra TUTTE le slide insieme (come il
 *   vecchio sito, dove l'animazione era della sezione intera); lab-reveal.js da solo animerebbe una slide fuori
 *   dalla pista (ritaglio dello scroller) solo quando ci scorre dentro, lasciandola trasparente per ~1s;
 * - nell'editor del tema niente autoplay ne loop, e un blocco selezionato viene portato in vista.
 *
 * Il custom element si reinizializza da solo quando l'editor ricarica la sezione (`shopify:section:load`
 * inserisce un nuovo elemento nel DOM).
 */

/** Tolleranza in px per i valori frazionari di scrollLeft / larghezze. */
const TOLERANCE = 2;

/** Durata massima in ms di un movimento animato, per sbloccare la posizione "in arrivo". */
const MOVE_TIMEOUT = 800;

/** Video con l'audio acceso in questo momento (uno solo in tutta la pagina). */
let audioVideo = null;

class LabSlider extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLButtonElement[]} */
  #prevButtons = [];

  /** @type {HTMLButtonElement[]} */
  #nextButtons = [];

  /** @type {HTMLElement | null} */
  #dotsContainer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  /** @type {IntersectionObserver | null} */
  #viewObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  #frame = 0;
  #step = 0;
  #max = 0;
  #positions = 1;
  #index = 0;
  #scrollable = false;
  #loop = false;
  #designMode = false;

  /** @type {number | null} */
  #pendingIndex = null;
  #pendingTimer = 0;

  #autoplayTimer = 0;

  /** @type {Set<string>} */
  #pauseReasons = new Set();

  connectedCallback() {
    const track = this.querySelector('[data-lab-track]');
    if (!(track instanceof HTMLElement)) return;

    this.#track = track;
    this.#viewport = this.querySelector('[data-lab-viewport]');
    this.#prevButtons = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-lab-prev]')));
    this.#nextButtons = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-lab-next]')));
    this.#dotsContainer = this.querySelector('[data-lab-dots]');
    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.#designMode =
      this.dataset.designMode === 'true' ||
      document.documentElement.classList.contains('shopify-design-mode') ||
      window.Shopify?.designMode === true;
    this.#loop = this.dataset.loop === 'true' && !this.#designMode;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    track.addEventListener('scrollend', this.#clearPending, { signal });
    this.#prevButtons.forEach((button) => button.addEventListener('click', () => this.#userMove(-1), { signal }));
    this.#nextButtons.forEach((button) => button.addEventListener('click', () => this.#userMove(1), { signal }));
    this.#dotsContainer?.addEventListener('click', this.#handleDotClick, { signal });
    this.addEventListener('click', this.#handleSoundClick, { signal });
    this.addEventListener('shopify:block:select', this.#showSelectedBlock, { signal });
    this.#mobileQuery.addEventListener('change', this.#scheduleUpdate, { signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
      this.#resizeObserver.observe(track);
      Array.from(track.children).forEach((slide) => this.#resizeObserver?.observe(slide));
    } else {
      window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    }

    this.#setupVideos();
    this.#setupAutoplay();
    this.#setupReveal();
    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
    this.#viewObserver?.disconnect();
    this.#viewObserver = null;
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;

    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = 0;
    window.clearTimeout(this.#pendingTimer);
    this.#pendingTimer = 0;

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }

    // Un video con l'audio acceso non deve restare "orfano" se la sezione viene rimossa
    if (audioVideo && this.contains(audioVideo)) {
      audioVideo.muted = true;
      audioVideo = null;
    }

    this.#track = null;
    this.#viewport = null;
    this.#prevButtons = [];
    this.#nextButtons = [];
    this.#dotsContainer = null;
  }

  // ---- Geometria e stato ---------------------------------------------------------------------------------------

  #scheduleUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  /** Ricalcola passo, posizioni e posizione corrente, poi aggiorna comandi e altezza. */
  #update() {
    const track = this.#track;
    if (!track) return;

    const style = getComputedStyle(track);
    const isGrid = style.display === 'grid';
    const slide = track.firstElementChild;
    const gap = parseFloat(style.columnGap) || 0;
    const slideWidth = slide ? slide.getBoundingClientRect().width : 0;

    this.#step = slideWidth + gap;
    this.#max = Math.max(0, track.scrollWidth - track.clientWidth);
    this.#scrollable = !isGrid && this.#step > 0 && this.#max > TOLERANCE;
    this.#positions = this.#scrollable ? Math.max(1, Math.round(this.#max / this.#step) + 1) : 1;

    const position = Math.abs(track.scrollLeft);
    this.#index = this.#scrollable ? clamp(Math.round(position / this.#step), 0, this.#positions - 1) : 0;

    this.classList.toggle('is-scrollable', this.#scrollable);
    this.#renderDots();
    this.#updateButtons(position);
    this.#updateHeight();
  }

  /** Crea i pallini se il numero delle posizioni e cambiato, poi segna quello attivo. */
  #renderDots() {
    const container = this.#dotsContainer;
    if (!container) return;

    const count = this.#scrollable ? this.#positions : 0;
    if (container.children.length !== count) {
      const label = this.dataset.labelDot || '';
      container.replaceChildren();

      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lab-slider__dot';
        dot.dataset.labDot = String(i);
        dot.setAttribute('aria-label', `${label} ${i + 1}`.trim());
        container.append(dot);
      }
    }

    Array.from(container.children).forEach((dot, i) => {
      const active = i === this.#index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /** @param {number} position scrollLeft assoluto */
  #updateButtons(position) {
    const wraps = this.#loop && this.#scrollable;
    const atStart = position <= TOLERANCE;
    const atEnd = position >= this.#max - TOLERANCE;

    this.#prevButtons.forEach((button, i) => {
      this.#setDisabled(button, this.#nextButtons[i], !this.#scrollable || (!wraps && atStart));
    });
    this.#nextButtons.forEach((button, i) => {
      this.#setDisabled(button, this.#prevButtons[i], !this.#scrollable || (!wraps && atEnd));
    });
  }

  /**
   * Imposta `disabled`; se il pulsante aveva il focus lo passa all'altro, cosi chi naviga da tastiera
   * non lo perde arrivando alla prima o all'ultima posizione.
   * @param {HTMLButtonElement} button
   * @param {HTMLButtonElement | undefined} other
   * @param {boolean} disabled
   */
  #setDisabled(button, other, disabled) {
    if (disabled && !button.disabled && document.activeElement === button && other && !other.disabled) {
      other.focus();
    }

    button.disabled = disabled;
  }

  /** Altezza adattiva (solo mobile): quella delle slide visibili, la piu alta se sono piu di una. */
  #updateHeight() {
    const viewport = this.#viewport;
    const track = this.#track;
    if (!viewport || !track) return;

    const adaptive = this.dataset.adaptive === 'true' && this.#mobileQuery?.matches && this.#scrollable;
    if (!adaptive) {
      viewport.style.height = '';
      return;
    }

    const columns = Math.max(1, Number(this.dataset.columnsMobile) || 1);
    const slides = Array.from(track.children);
    let height = 0;
    for (let i = this.#index; i < Math.min(slides.length, this.#index + columns); i += 1) {
      height = Math.max(height, /** @type {HTMLElement} */ (slides[i]).offsetHeight);
    }

    viewport.style.height = height ? `${height}px` : '';
  }

  // ---- Movimento -----------------------------------------------------------------------------------------------

  /**
   * Va alla posizione indicata (con scorrimento fluido, a meno che l'utente non riduca i movimenti).
   * @param {number} index
   */
  #goTo(index) {
    const track = this.#track;
    if (!track || !this.#scrollable) return;

    const target = clamp(index, 0, this.#positions - 1);
    const offset = Math.min(target * this.#step, this.#max);
    const sign = getComputedStyle(track).direction === 'rtl' ? -1 : 1;

    this.#pendingIndex = target;
    window.clearTimeout(this.#pendingTimer);
    this.#pendingTimer = window.setTimeout(this.#clearPending, MOVE_TIMEOUT);

    track.scrollTo({ left: sign * offset, behavior: this.#motionQuery?.matches ? 'auto' : 'smooth' });
  }

  #clearPending = () => {
    this.#pendingIndex = null;
    window.clearTimeout(this.#pendingTimer);
    this.#pendingTimer = 0;
  };

  /**
   * Una posizione avanti o indietro; con il loop si passa dall'ultima alla prima e viceversa.
   * Parte dalla posizione "in arrivo" se un movimento e ancora in corso, cosi clic ravvicinati si sommano.
   * @param {1 | -1} direction
   */
  #move(direction) {
    if (!this.#scrollable) return;

    let target = (this.#pendingIndex ?? this.#index) + direction;
    if (target < 0) target = this.#loop ? this.#positions - 1 : 0;
    if (target > this.#positions - 1) target = this.#loop ? 0 : this.#positions - 1;

    this.#goTo(target);
  }

  /**
   * Mossa dell'utente: sposta e fa ripartire il conteggio dell'autoplay.
   * @param {1 | -1} direction
   */
  #userMove(direction) {
    this.#move(direction);
    this.#restartAutoplay();
  }

  /** @param {Event} event */
  #handleDotClick = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('[data-lab-dot]') : null;
    if (!(dot instanceof HTMLElement)) return;

    this.#goTo(Number(dot.dataset.labDot));
    this.#restartAutoplay();
  };

  /**
   * Nell'editor del tema, selezionando un blocco la sua slide viene portata in vista.
   * @param {Event} event
   */
  #showSelectedBlock = (event) => {
    const target = event.target;
    if (!this.#scrollable || !(target instanceof Element)) return;

    target.closest('.lab-slider__slide')?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  };

  // ---- Autoplay ------------------------------------------------------------------------------------------------

  #setupAutoplay() {
    const delay = Number(this.dataset.autoplay) || 0;
    if (!delay || this.#designMode || this.#motionQuery?.matches) return;

    const signal = this.#abort?.signal;
    const pause = (/** @type {string} */ reason) => () => this.#pauseReasons.add(reason);
    const resume = (/** @type {string} */ reason) => () => this.#pauseReasons.delete(reason);

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.#pauseReasons.add('pointer');
    }, { signal });
    this.addEventListener('pointerleave', resume('pointer'), { signal });
    this.addEventListener('focusin', pause('focus'), { signal });
    this.addEventListener('focusout', resume('focus'), { signal });
    this.addEventListener('touchstart', pause('touch'), { passive: true, signal });
    this.addEventListener('touchend', resume('touch'), { passive: true, signal });
    this.addEventListener('touchcancel', resume('touch'), { passive: true, signal });

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) this.#pauseReasons.add('hidden');
        else this.#pauseReasons.delete('hidden');
      },
      { signal }
    );

    if ('IntersectionObserver' in window) {
      this.#viewObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#pauseReasons.delete('offscreen');
          else this.#pauseReasons.add('offscreen');
        }
      });
      this.#viewObserver.observe(this);
    }

    this.#restartAutoplay();
  }

  #restartAutoplay() {
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = 0;

    const delay = Number(this.dataset.autoplay) || 0;
    if (!delay || this.#designMode || this.#motionQuery?.matches) return;

    this.#autoplayTimer = window.setInterval(() => {
      if (this.#pauseReasons.size > 0 || !this.#scrollable) return;

      // Senza loop si ferma sull'ultima posizione
      if (!this.#loop && this.#index >= this.#positions - 1) return;

      this.#move(1);
    }, delay);
  }

  // ---- Animazione d'ingresso -----------------------------------------------------------------------------------

  /**
   * Mostra tutte le slide (`is-shown`, classe di lab-reveal.js) appena il componente entra nella finestra, con lo
   * stesso margine di -50px in basso di lab-reveal.js. Le slide fuori dalla pista non intersecano mai il viewport
   * (lo scroller le ritaglia): senza questo passaggio comparirebbero solo quando l'utente ci scorre dentro, dopo il
   * ritardo a cascata (150ms + 150ms per indice) e la dissolvenza di 650ms.
   */
  #setupReveal() {
    if (!('IntersectionObserver' in window)) return;

    const slides = Array.from(this.querySelectorAll('.lab-slider__slide[data-lab-reveal]'));
    if (slides.length === 0) return;

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        slides.forEach((slide) => slide.classList.add('is-shown'));
        this.#revealObserver?.disconnect();
        this.#revealObserver = null;
      },
      { root: null, rootMargin: '0px 0px -50px 0px', threshold: 0 }
    );
    this.#revealObserver.observe(this);
  }

  // ---- Video ---------------------------------------------------------------------------------------------------

  #setupVideos() {
    const videos = /** @type {HTMLVideoElement[]} */ (Array.from(this.querySelectorAll('video[data-lab-video]')));
    if (videos.length === 0) return;

    const signal = this.#abort?.signal;

    // Il pulsante audio segue lo stato reale del video (anche se cambia dall'esterno)
    videos.forEach((video) => video.addEventListener('volumechange', () => this.#syncSoundButton(video), { signal }));

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => this.#startVideo(video));
      return;
    }

    // Il clipping dello scroller conta: una slide fuori dalla pista non interseca il viewport
    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) this.#startVideo(video);
          else this.#stopVideo(video);
        }
      },
      { rootMargin: '200px' }
    );
    videos.forEach((video) => this.#videoObserver?.observe(video));
  }

  /**
   * Assegna la sorgente la prima volta e avvia il video (muto, a meno che l'utente non abbia acceso l'audio).
   * @param {HTMLVideoElement} video
   */
  #startVideo(video) {
    if (video.dataset.labLoaded !== 'true') {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!(source instanceof HTMLSourceElement)) return;
        source.src = source.dataset.src || '';
        source.removeAttribute('data-src');
      });
      video.dataset.labLoaded = 'true';
      video.load();
    }

    video.play()?.catch(() => {});
  }

  /**
   * Ferma il video fuori vista e ne spegne l'audio.
   * @param {HTMLVideoElement} video
   */
  #stopVideo(video) {
    video.pause();
    if (!video.muted) video.muted = true;
  }

  /**
   * Tasto audio: accende l'audio del video della slide (spegnendo quello di un altro video) oppure lo spegne.
   * @param {Event} event
   */
  #handleSoundClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-lab-sound]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const video = button.closest('.lab-slider__media')?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    if (video.muted) {
      if (audioVideo && audioVideo !== video) audioVideo.muted = true;
      audioVideo = video;
      video.muted = false;
      // Il gesto dell'utente permette di avviare il video anche se l'autoplay era stato bloccato
      if (video.paused) this.#startVideo(video);
    } else {
      video.muted = true;
      if (audioVideo === video) audioVideo = null;
    }

    this.#syncSoundButton(video);
  };

  /** @param {HTMLVideoElement} video */
  #syncSoundButton(video) {
    const button = video.closest('.lab-slider__media')?.querySelector('[data-lab-sound]');
    if (!(button instanceof HTMLButtonElement)) return;

    const soundOn = !video.muted;
    button.setAttribute('aria-pressed', String(soundOn));
    button.setAttribute('aria-label', (soundOn ? button.dataset.labelMute : button.dataset.labelUnmute) || '');
    if (!soundOn && audioVideo === video) audioVideo = null;
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('lab-slider')) {
  customElements.define('lab-slider', LabSlider);
}
